import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';

/**
 * ServiceTypeComparisonView
 * Allows comparison of provider rates for a selected service type
 * 
 * @param {Array} providers - All providers with their service rates
 * @param {Array} activeServiceTypes - All active service types
 */
function ServiceTypeComparisonView({ providers = [], activeServiceTypes = [] }) {
  const navigate = useNavigate();
  const [selectedServiceType, setSelectedServiceType] = useState('');
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');

  // Auto-select first service type if available
  useEffect(() => {
    if (activeServiceTypes.length > 0 && !selectedServiceType) {
      setSelectedServiceType(activeServiceTypes[0].id);
    }
  }, [activeServiceTypes, selectedServiceType]);

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return `$${parseFloat(value).toFixed(2)}`;
  };

  const handleServiceTypeChange = (event) => {
    setSelectedServiceType(event.target.value);
  };

  const handleEditProvider = (providerId) => {
    navigate(`/provider/edit/${providerId}`);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  if (activeServiceTypes.length === 0) {
    return (
      <Alert severity="info">
        No active service types available for comparison.
      </Alert>
    );
  }

  const selectedType = activeServiceTypes.find(st => st.id === selectedServiceType);
  if (!selectedType) return null;

  const isInitialServiceCall = selectedType.name === 'Initial Service Call';

  // Prepare comparison data
  const comparisonData = providers.map(provider => {
    const config = (provider.provider_service_rates || []).find(
      rate => rate.service_type_id === selectedServiceType
    );
    
    return {
      id: provider.id,
      name: provider.name,
      email: provider.email,
      configured: !!config,
      rateType: config?.rate_type || '-',
      config: config || null,
      // For sorting purposes
      sortableRate: config?.flat_rate || config?.hourly_rate || config?.per_location || 0
    };
  });

  // Sorting logic
  const sortedData = [...comparisonData].sort((a, b) => {
    if (orderBy === 'name') {
      return order === 'asc' 
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name);
    } else if (orderBy === 'configured') {
      const aVal = a.configured ? 1 : 0;
      const bVal = b.configured ? 1 : 0;
      return order === 'asc' ? aVal - bVal : bVal - aVal;
    } else if (orderBy === 'rate') {
      return order === 'asc' 
        ? a.sortableRate - b.sortableRate
        : b.sortableRate - a.sortableRate;
    }
    return 0;
  });

  const renderRateDetails = (item) => {
    if (!item.configured) {
      return (
        <TableCell>
          <Chip label="Not Configured" size="small" color="warning" />
        </TableCell>
      );
    }

    const config = item.config;

    return (
      <TableCell>
        {config.rate_type === 'HOURLY' ? (
          <Box>
            <Typography variant="body2">
              {formatCurrency(config.hourly_rate)}/hour
            </Typography>
          </Box>
        ) : isInitialServiceCall ? (
          <Box>
            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
              Board: {formatCurrency(config.per_board)}, 
              Loc: {formatCurrency(config.per_location)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Units: {formatCurrency(config.rate_1_unit)} - {formatCurrency(config.rate_10_units)}
              {' | '}Refusal: {formatCurrency(config.refusal_rate)}
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2">
              Completion: {formatCurrency(config.flat_rate)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Refusal: {formatCurrency(config.refusal_rate)}
            </Typography>
          </Box>
        )}
      </TableCell>
    );
  };

  // Calculate statistics
  const configuredCount = comparisonData.filter(d => d.configured).length;
  const missingCount = comparisonData.length - configuredCount;

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Service Type Rate Comparison
        </Typography>
        
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Select Service Type</InputLabel>
          <Select
            value={selectedServiceType}
            onChange={handleServiceTypeChange}
            label="Select Service Type"
          >
            {activeServiceTypes.map(serviceType => (
              <MenuItem key={serviceType.id} value={serviceType.id}>
                {serviceType.name} ({serviceType.code})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Chip 
            label={`${configuredCount} Configured`} 
            color="success" 
            size="small"
          />
          <Chip 
            label={`${missingCount} Missing`} 
            color="warning" 
            size="small"
          />
        </Box>

        {selectedType.description && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {selectedType.description}
          </Alert>
        )}
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleRequestSort('name')}
                >
                  Provider Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'configured'}
                  direction={orderBy === 'configured' ? order : 'asc'}
                  onClick={() => handleRequestSort('configured')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>Rate Type</TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'rate'}
                  direction={orderBy === 'rate' ? order : 'asc'}
                  onClick={() => handleRequestSort('rate')}
                >
                  Rate Details
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map(item => (
              <TableRow 
                key={item.id}
                sx={{ 
                  backgroundColor: item.configured ? 'inherit' : '#fff3e0',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {item.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={item.configured ? 'Configured' : 'Missing'}
                    size="small"
                    color={item.configured ? 'success' : 'warning'}
                  />
                </TableCell>
                <TableCell>
                  {item.configured ? (
                    <Chip 
                      label={item.rateType}
                      size="small"
                      color={item.rateType === 'FLAT' ? 'primary' : 'secondary'}
                    />
                  ) : (
                    '-'
                  )}
                </TableCell>
                {renderRateDetails(item)}
                <TableCell align="center">
                  <Tooltip title="Edit Provider">
                    <IconButton 
                      size="small" 
                      color="primary"
                      onClick={() => handleEditProvider(item.id)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default ServiceTypeComparisonView;

